import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, PhoneOff, Video, MessageSquare, X } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface InAppNotification {
  id: string;
  type: "typing" | "call" | "video-call" | "message";
  displayName: string;
  avatarUrl: string | null;
  chatId: string;
}

// Ringtone using Web Audio API
const createRingtone = () => {
  let ctx: AudioContext | null = null;
  let intervalId: ReturnType<typeof setInterval> | null = null;
  let vibrationId: ReturnType<typeof setInterval> | null = null;

  const start = () => {
    // Vibration
    if ("vibrate" in navigator) {
      navigator.vibrate([300, 200, 300, 200, 300]);
      vibrationId = setInterval(() => {
        navigator.vibrate([300, 200, 300, 200, 300]);
      }, 2000);
    }

    // Audio ringtone
    try {
      ctx = new AudioContext();
      const playTone = () => {
        if (!ctx) return;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 440;
        osc.type = "sine";
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.8);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.8);
      };
      playTone();
      intervalId = setInterval(playTone, 1500);
    } catch (e) {
      console.warn("Audio ringtone not supported:", e);
    }
  };

  const stop = () => {
    if (intervalId) { clearInterval(intervalId); intervalId = null; }
    if (vibrationId) { clearInterval(vibrationId); vibrationId = null; }
    if ("vibrate" in navigator) navigator.vibrate(0);
    if (ctx) { ctx.close().catch(() => {}); ctx = null; }
  };

  return { start, stop };
};

export const GlobalNotifications = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [chatProfiles, setChatProfiles] = useState<
    Map<string, { chatId: string; displayName: string; avatarUrl: string | null; participantIds: string[] }>
  >(new Map());
  const channelsRef = useRef<ReturnType<typeof supabase.channel>[]>([]);
  const typingTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const notifTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const ringtoneRef = useRef<ReturnType<typeof createRingtone> | null>(null);

  // Get current user
  useEffect(() => {
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUserId(session?.user?.id || null);
    };
    getUser();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id || null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Fetch user's chats and participant profiles
  useEffect(() => {
    if (!userId) return;

    const fetchChats = async () => {
      const { data: participantData } = await supabase
        .from("chat_participants")
        .select("chat_id")
        .eq("user_id", userId);

      if (!participantData?.length) return;

      const chatIds = participantData.map(p => p.chat_id);

      const { data: chatsData } = await supabase
        .from("chats")
        .select("*")
        .in("id", chatIds);

      if (!chatsData) return;

      const profileMap = new Map<string, { chatId: string; displayName: string; avatarUrl: string | null; participantIds: string[] }>();

      await Promise.all(
        chatsData.map(async (chat) => {
          const { data: participants } = await supabase
            .from("chat_participants")
            .select("user_id")
            .eq("chat_id", chat.id);

          const otherIds = (participants || []).map(p => p.user_id).filter(id => id !== userId);

          let displayName = chat.group_name || "Gruppo";
          let avatarUrl: string | null = chat.group_image_url;

          if (!chat.is_group && otherIds.length > 0) {
            const { data: profile } = await supabase
              .from("profiles")
              .select("first_name, last_name, avatar_url, email")
              .eq("user_id", otherIds[0])
              .single();

            if (profile) {
              const name = [profile.first_name, profile.last_name].filter(Boolean).join(" ");
              displayName = name || profile.email || "Utente";
              avatarUrl = profile.avatar_url;
            }
          }

          profileMap.set(chat.id, { chatId: chat.id, displayName, avatarUrl, participantIds: otherIds });
        })
      );

      setChatProfiles(profileMap);
    };

    fetchChats();
  }, [userId]);

  // Start/stop ringtone based on active call notifications
  useEffect(() => {
    const hasCallNotif = notifications.some(n => n.type === "call" || n.type === "video-call");
    if (hasCallNotif) {
      if (!ringtoneRef.current) {
        ringtoneRef.current = createRingtone();
        ringtoneRef.current.start();
      }
    } else {
      if (ringtoneRef.current) {
        ringtoneRef.current.stop();
        ringtoneRef.current = null;
      }
    }
  }, [notifications]);

  // Cleanup ringtone on unmount
  useEffect(() => {
    return () => {
      ringtoneRef.current?.stop();
    };
  }, []);

  const showNotification = useCallback((notif: InAppNotification, durationMs = 4000) => {
    setNotifications(prev => {
      const filtered = prev.filter(n => !(n.chatId === notif.chatId && n.type === notif.type));
      return [...filtered, notif];
    });

    const existing = notifTimersRef.current.get(notif.id);
    if (existing) clearTimeout(existing);

    const timer = setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== notif.id));
      notifTimersRef.current.delete(notif.id);
    }, durationMs);
    notifTimersRef.current.set(notif.id, timer);
  }, []);

  const dismissNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
    const timer = notifTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      notifTimersRef.current.delete(id);
    }
  }, []);

  // Subscribe to all chat channels for typing & call signals
  useEffect(() => {
    if (!userId || chatProfiles.size === 0) return;

    channelsRef.current.forEach(ch => supabase.removeChannel(ch));
    channelsRef.current = [];

    chatProfiles.forEach((info, chatId) => {
      const typingCh = supabase
        .channel(`global-typing-${chatId}`)
        .on("broadcast", { event: "typing" }, (payload) => {
          const senderId = payload.payload?.user_id;
          if (senderId === userId) return;

          const key = `typing-${chatId}`;
          const existingTimer = typingTimersRef.current.get(key);
          if (existingTimer) return;

          showNotification({
            id: key, type: "typing",
            displayName: info.displayName, avatarUrl: info.avatarUrl, chatId,
          }, 3000);

          typingTimersRef.current.set(key, setTimeout(() => {
            typingTimersRef.current.delete(key);
          }, 3000));
        })
        .subscribe();

      const callCh = supabase
        .channel(`call-${chatId}`)
        .on("broadcast", { event: "incoming-call" }, (msg) => {
          if (msg.payload?.from === userId) return;
          const isVideo = msg.payload?.isVideo || false;

          showNotification({
            id: `call-${chatId}`,
            type: isVideo ? "video-call" : "call",
            displayName: info.displayName, avatarUrl: info.avatarUrl, chatId,
          }, 15000);
        })
        .on("broadcast", { event: "end-call" }, () => {
          dismissNotification(`call-${chatId}`);
        })
        .subscribe();

      const msgCh = supabase
        .channel(`global-msg-${chatId}`)
        .on("postgres_changes", {
          event: "INSERT", schema: "public", table: "messages",
          filter: `chat_id=eq.${chatId}`,
        }, (payload) => {
          const msg = payload.new as any;
          if (msg.sender_id === userId) return;

          showNotification({
            id: `msg-${chatId}-${msg.id}`, type: "message",
            displayName: info.displayName, avatarUrl: info.avatarUrl, chatId,
          }, 4000);
        })
        .subscribe();

      channelsRef.current.push(typingCh, callCh, msgCh);
    });

    return () => {
      channelsRef.current.forEach(ch => supabase.removeChannel(ch));
      channelsRef.current = [];
      typingTimersRef.current.forEach(t => clearTimeout(t));
      typingTimersRef.current.clear();
      notifTimersRef.current.forEach(t => clearTimeout(t));
      notifTimersRef.current.clear();
    };
  }, [userId, chatProfiles, showNotification, dismissNotification]);

  const handleAcceptCall = useCallback((notif: InAppNotification) => {
    dismissNotification(notif.id);
    const isVideo = notif.type === "video-call";
    navigate(`/messaggi?chat=${notif.chatId}&answer=${isVideo ? "video" : "audio"}`);
  }, [dismissNotification, navigate]);

  const handleDeclineCall = useCallback((notif: InAppNotification) => {
    dismissNotification(notif.id);
    // Broadcast end-call to the caller
    supabase.channel(`call-${notif.chatId}`).send({
      type: "broadcast",
      event: "end-call",
      payload: { from: userId },
    });
  }, [dismissNotification, userId]);

  const handleNotificationClick = useCallback((notif: InAppNotification) => {
    if (notif.type === "call" || notif.type === "video-call") return; // handled by buttons
    dismissNotification(notif.id);
    navigate(`/messaggi?chat=${notif.chatId}`);
  }, [dismissNotification, navigate]);

  if (notifications.length === 0) return null;

  const getIcon = (type: InAppNotification["type"]) => {
    switch (type) {
      case "typing": return <MessageSquare className="w-5 h-5" />;
      case "call": return <Phone className="w-5 h-5" />;
      case "video-call": return <Video className="w-5 h-5" />;
      case "message": return <MessageSquare className="w-5 h-5" />;
    }
  };

  const getMessage = (type: InAppNotification["type"], name: string) => {
    switch (type) {
      case "typing": return `${name} sta scrivendo...`;
      case "call": return `${name} ti sta chiamando`;
      case "video-call": return `${name} ti sta videochiamando`;
      case "message": return `Nuovo messaggio da ${name}`;
    }
  };

  const getAccentColor = (type: InAppNotification["type"]) => {
    switch (type) {
      case "call": return "text-green-400";
      case "video-call": return "text-blue-400";
      default: return "text-primary";
    }
  };

  const isCallNotif = (type: InAppNotification["type"]) => type === "call" || type === "video-call";

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] flex flex-col gap-2 w-[92%] max-w-sm pointer-events-none">
      <AnimatePresence>
        {notifications.map(notif => (
          <motion.div
            key={notif.id}
            initial={{ opacity: 0, y: -40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={() => handleNotificationClick(notif)}
            className={`pointer-events-auto backdrop-blur-xl border border-border/40 rounded-2xl px-4 py-3 shadow-2xl transition-all ${
              isCallNotif(notif.type)
                ? "bg-card/95 cursor-default"
                : "bg-card/90 cursor-pointer hover:bg-card/95 active:scale-[0.98]"
            }`}
          >
            <div className="flex items-center gap-3">
              <Avatar className="w-10 h-10 flex-shrink-0">
                <AvatarImage src={notif.avatarUrl || undefined} />
                <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                  {notif.displayName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{notif.displayName}</p>
                <div className="flex items-center gap-1.5">
                  <span className={getAccentColor(notif.type)}>{getIcon(notif.type)}</span>
                  <p className="text-xs text-muted-foreground truncate">
                    {getMessage(notif.type, notif.displayName)}
                  </p>
                </div>
              </div>

              {!isCallNotif(notif.type) && (
                <button
                  onClick={(e) => { e.stopPropagation(); dismissNotification(notif.id); }}
                  className="p-1 rounded-full hover:bg-muted/40 transition-colors flex-shrink-0"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
            </div>

            {/* Accept / Decline buttons for calls */}
            {isCallNotif(notif.type) && (
              <div className="flex items-center justify-center gap-6 mt-3 pt-3 border-t border-border/20">
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeclineCall(notif); }}
                  className="flex items-center gap-2 px-5 py-2 rounded-full bg-red-500 text-white text-sm font-medium shadow-lg shadow-red-500/20 hover:bg-red-600 active:scale-95 transition-all"
                >
                  <PhoneOff className="w-4 h-4" />
                  Rifiuta
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleAcceptCall(notif); }}
                  className="flex items-center gap-2 px-5 py-2 rounded-full bg-green-500 text-white text-sm font-medium shadow-lg shadow-green-500/20 hover:bg-green-600 active:scale-95 transition-all"
                >
                  {notif.type === "video-call" ? <Video className="w-4 h-4" /> : <Phone className="w-4 h-4" />}
                  Rispondi
                </button>
              </div>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};
